import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../layout/TopBar';
import { useHGS } from '../context/HGSContext';
import { buildMyTasks, buildMyRisks } from '../data/rbac';
import { DEPARTMENTS } from '../data/mockData';
import { computeResidualScore, ragFor, bandFor } from '../data/riskUniverse';
import RiskUnpublishedGate from '../components/RiskUnpublishedGate';
import {
  ClipboardList, AlertTriangle, ShieldAlert, Clock, CheckCircle2, FileCheck2,
  FileSignature, ShieldCheck, ChevronRight, Send, Filter, Building2, Flag, Lock,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const MODULE_STYLES = {
  PCC: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', icon: ClipboardList, route: '/pcc' },
  Fraud: { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', icon: ShieldCheck, route: '/fraud' },
  DLC: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: FileSignature, route: '/dlc' },
};

const STATUS_STYLE = {
  Draft: 'bg-slate-100 text-slate-700',
  Submitted: 'bg-blue-100 text-blue-800',
  UnderReview: 'bg-amber-100 text-amber-800',
  Approved: 'bg-emerald-100 text-emerald-800',
  FinalApproved: 'bg-emerald-500 text-white',
  Rejected: 'bg-red-100 text-red-800',
};

export default function MyTasksPage() {
  const { user, masters, formResponses, riskUniverse, riskPublished } = useHGS();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');

  const dept = DEPARTMENTS.find((d) => d.id === user?.department);

  const tasks = useMemo(() => buildMyTasks({ user, masters, formResponses }), [user, masters, formResponses]);
  const myRisks = useMemo(() => buildMyRisks({ user, riskUniverse }).map((r) => {
    const { inherent, residual } = computeResidualScore(r);
    return { ...r, residual, rag: ragFor(residual), band: bandFor(residual) };
  }), [user, riskUniverse]);

  const filtered = useMemo(() => {
    switch (filter) {
      case 'pending': return tasks.filter((t) => t.status === 'Draft');
      case 'submitted': return tasks.filter((t) => t.status === 'Submitted' || t.status === 'UnderReview');
      case 'overdue': return tasks.filter((t) => t.overdue);
      case 'pcc': return tasks.filter((t) => t.module === 'PCC');
      case 'fraud': return tasks.filter((t) => t.module === 'Fraud');
      case 'dlc': return tasks.filter((t) => t.module === 'DLC');
      default: return tasks;
    }
  }, [tasks, filter]);

  const stats = useMemo(() => {
    const pending = tasks.filter((t) => t.status === 'Draft').length;
    const submitted = tasks.filter((t) => t.status !== 'Draft').length;
    const overdue = tasks.filter((t) => t.overdue && t.status === 'Draft').length;
    const approved = tasks.filter((t) => t.status === 'FinalApproved').length;
    return { total: tasks.length, pending, submitted, overdue, approved };
  }, [tasks]);

  const rag = (r) => r.band === 'High' ? 'Red' : r.band === 'Medium' ? 'Amber' : 'Green';

  return (
    <div>
      <TopBar
        title={`My Dashboard — ${dept?.label || 'Department'}`}
        subtitle={`Hi ${user?.name?.split(' ')[0] || ''}, here's everything assigned to ${dept?.label || 'your department'} this quarter`}
        breadcrumbs={['HGS', 'My Tasks']}
      />

      <div className="p-8 space-y-5">
        {/* Department identity banner */}
        <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-slate-900 to-[#1a0000] text-white" data-testid="my-dept-banner">
          <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center" style={{ borderLeft: `3px solid ${dept?.color || '#CC0000'}` }}>
            <Building2 className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Signed in as</div>
            <div className="text-sm font-bold">{user?.name} · Department Head · {dept?.label}</div>
            <div className="text-[11px] text-slate-400 mt-0.5">You can only see items assigned to <b>{dept?.label}</b>. Submit each task for Division Head review.</div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 text-[10px] font-bold uppercase tracking-wider text-white">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            Secure view
          </span>
        </div>

        {/* Gate: Risk Universe not published → block tasks */}
        {!riskPublished?.published && <RiskUnpublishedGate user={user} />}

        {riskPublished?.published && (<>
        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatTile label="Total Tasks" value={stats.total} icon={ClipboardList} tone="slate" testId="stat-total" />
          <StatTile label="Pending" value={stats.pending} icon={Clock} tone="amber" testId="stat-pending" />
          <StatTile label="Submitted" value={stats.submitted} icon={Send} tone="sky" testId="stat-submitted" />
          <StatTile label="Overdue" value={stats.overdue} icon={AlertTriangle} tone="red" testId="stat-overdue" />
          <StatTile label="Approved" value={stats.approved} icon={CheckCircle2} tone="emerald" testId="stat-approved" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr,380px] gap-5">
          {/* My Tasks */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="my-tasks-card">
            <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold text-slate-900">My Tasks</h3>
                <p className="text-[11px] text-slate-500">Overdue first · click a task to open the module form</p>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Filter className="w-3.5 h-3.5 text-slate-400" />
                {[
                  { k: 'all', l: 'All' },
                  { k: 'pending', l: 'Pending' },
                  { k: 'submitted', l: 'Submitted' },
                  { k: 'overdue', l: 'Overdue' },
                  { k: 'pcc', l: 'PCC' },
                  { k: 'fraud', l: 'Fraud' },
                  { k: 'dlc', l: 'DLC' },
                ].map((f) => (
                  <button
                    key={f.k}
                    data-testid={`filter-${f.k}`}
                    onClick={() => setFilter(f.k)}
                    className={cn(
                      'px-2 py-1 rounded-md text-[11px] font-bold transition',
                      filter === f.k ? 'bg-[#CC0000] text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                    )}
                  >
                    {f.l}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="p-12 text-center" data-testid="tasks-empty">
                <FileCheck2 className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="text-sm font-bold text-slate-700">No tasks match this filter</p>
                <p className="text-xs text-slate-500 mt-1">Clear the filter or check back after the master refresh.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[640px] overflow-y-auto">
                {filtered.map((t) => {
                  const M = MODULE_STYLES[t.module];
                  return (
                    <button
                      key={`${t.moduleKey}-${t.id}`}
                      data-testid={`task-row-${t.id}`}
                      onClick={() => navigate(M.route)}
                      className="group text-left w-full p-4 flex items-start gap-3 hover:bg-slate-50 transition"
                    >
                      <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border', M.bg, M.text, M.border)}>
                        <M.icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-bold uppercase', M.bg, M.text)}>{t.module}</span>
                          <span className="font-mono text-[10px] text-slate-500">{t.id}</span>
                          <span className="text-slate-300">·</span>
                          <span className="text-[10px] text-slate-500">{t.section}</span>
                          {t.linkedRiskId && (
                            <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-bold">{t.linkedRiskId}</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-800 leading-snug">{t.title}</p>
                        <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                          <span>Due: <span className={cn('font-semibold', t.overdue ? 'text-red-700' : 'text-slate-700')}>{t.dueDate}</span></span>
                          <span>·</span>
                          <span>Owner: <span className="font-semibold text-slate-700">{t.ownerRole}</span></span>
                          {t.evidenceRequired === 'Yes' && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 text-[9px] font-bold">
                              Evidence required
                            </span>
                          )}
                          {t.overdue && t.status === 'Draft' && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-800 text-[9px] font-bold uppercase">
                              Overdue
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 flex flex-col items-end gap-1">
                        <span className={cn('px-2 py-0.5 rounded text-[10px] font-bold uppercase', STATUS_STYLE[t.status] || STATUS_STYLE.Draft)}>
                          {t.status}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#CC0000] group-hover:translate-x-0.5 transition" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* My Risks */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="my-risks-card">
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert className="w-4 h-4 text-[#CC0000]" />
                <h3 className="text-sm font-bold text-slate-900">Linked Risks</h3>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[9px] font-bold uppercase">
                  <Lock className="w-2.5 h-2.5" />
                  Read-only
                </span>
                <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{myRisks.length}</span>
              </div>
              <p className="text-[10px] text-slate-500 mb-2 leading-relaxed">
                Risk scoring is owned by the Operating Head. These are the risks mapped to {dept?.label} — context for your tasks.
              </p>
              {myRisks.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No risks currently mapped to {dept?.label}.</p>
              ) : (
                <div className="space-y-2 max-h-[320px] overflow-y-auto">
                  {myRisks.map((r) => (
                    <div key={r.id} className="p-2.5 rounded-lg border border-slate-100 bg-slate-50">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-mono text-[10px] font-bold text-slate-700">{r.id}</span>
                        <span className={cn('w-2 h-2 rounded-full',
                          rag(r) === 'Red' ? 'bg-red-500' : rag(r) === 'Amber' ? 'bg-amber-400' : 'bg-emerald-400'
                        )} />
                      </div>
                      <p className="text-[11px] text-slate-700 leading-snug">{r.description}</p>
                      <div className="mt-1.5 flex items-center gap-2 text-[9px] text-slate-500">
                        <span>Residual: <b className="text-slate-700 tabular-nums">{r.residual}</b></span>
                        <span>·</span>
                        <span>Band: <b className="text-slate-700">{r.band}</b></span>
                        {r.band === 'High' && <Flag className="w-2.5 h-2.5 text-red-600" />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        </>)}
      </div>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, tone = 'slate', testId }) {
  const toneMap = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    sky: 'bg-sky-50 text-sky-800 border-sky-200',
    red: 'bg-red-50 text-red-800 border-red-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  };
  return (
    <div className={cn('rounded-xl border p-4', toneMap[tone])} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 opacity-60" />}
      </div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
